import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/Login';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import EventList from './pages/EventList';
import EventDetail from './pages/EventDetail';
import Transactions from './pages/Transactions';
import AttendanceRecords from './pages/AttendanceRecords';
import IntegrityChecker from './pages/IntegrityChecker';
import IntegrityCheckerTxns from './pages/IntegrityCheckerTxns';
import Settings from './pages/Settings';
import MusterCount from './pages/MusterCount';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isInitializing } = useAuth();

    if (isInitializing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isInitializing } = useAuth();

    if (isInitializing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return user ? <Navigate to="/events" replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />

            {/* Protected Routes */}
            <Route
                path="/events"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EventList />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/events/:txHash"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EventDetail />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/transactions"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Transactions />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Dashboard />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/attendance"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <AttendanceRecords />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/integrity-checker"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <IntegrityChecker />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Settings />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/integrity-checker/:checkpointId/txns"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <IntegrityCheckerTxns />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/muster-count"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <MusterCount />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/events" replace />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/events" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <Router>
            <AuthProvider>
                <div className="App">
                    <AppRoutes />
                </div>
            </AuthProvider>
        </Router>
    );
}

export default App;
